"use client";

import type React from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";

import { useAuth } from "./hooks/useAuth";
import { useJwtExpiration } from "./hooks/useJwtExpiration";
import ProtectedRoute from "./components/common/ProtectedRoute";
import PublicRoute from "./components/common/PublicRoute";
import Layout from "./components/layout/Layout";
import Login from "./pages/Login";
import Register from "./pages/Register";
import SchoolSelection from "./pages/SchoolSelection";
import SchoolSelectionDashboard from "./pages/SchoolSelectionDashboard";
import Dashboard from "./pages/Dashboard";
import Students from "./pages/Students";
import StudentDetail from "./pages/StudentDetail";
import Teachers from "./pages/Teachers";
import TeacherDetail from "./pages/TeacherDetail";
import Classes from "./pages/Classes";
import ClassDetail from "./pages/ClassDetail";
import AdmitCards from "./pages/AdmitCards";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminSchools from "./pages/admin/AdminSchools";
import AdminUsers from "./pages/admin/AdminUsers";
import NotFound from "./pages/NotFound";
import AttendanceManagement from "components/TeacherDashboard/AttendanceManagement";
import UserManagement from "pages/admin/admin-panel/UserManagement";
import SchoolManagement from "pages/admin/admin-panel/SchoolManagement";
import StudentManagement from "pages/admin/admin-panel/StudentManagement";
import TeacherManagement from "pages/admin/admin-panel/TeacherManagement";
import SubjectManagement from "pages/admin/admin-panel/subjectManagement";
import ResultManagement from "pages/admin/admin-panel/ResultManagement";
import AdmitCardManagement from "pages/admin/admin-panel/AdmitCardManagement";
import IdCardManagement from "pages/admin/admin-panel/IdCardManagement";
import ApplicationFormManagement from "pages/admin/admin-panel/ApplicationFormManagement";
import ApplicationUserManagement from "pages/admin/admin-panel/ApplicationUserManagement";
import AdmissionApplicationFromManagement from "pages/AdmissionApplicationFromManagement";
import AadharCardManagement from "pages/admin/admin-panel/AadharCardManagement";
import StudentProfile from "pages/student/StudentProfile";
import FeePaymentManagement from "pages/student/FeePaymentManagement";
import StudentPromotion from "pages/student/StudentPromotion";
import StudentProfilePicker from "pages/student/StudentProfilePicker";
import { SessionClassProvider } from "contexts/SessionClassContext";
import ExamFormManagement from "pages/admin/admin-panel/ExamFormManagement";
import { SchoolProvider } from "./contexts/SchoolContext";
import { ErrorProvider } from "./contexts/ErrorContext";
import ClassManagement from "pages/admin/admin-panel/ClassManagement";
import SessionManagement from "pages/admin/admin-panel/SessionManagement";
import { useTenantHeader } from "./hooks/useTenantHeader";
import GosvaraSheetManagement from "pages/admin/admin-panel/GosvaraSheetManagement";
import MarkSheetMarksManagement from "pages/admin/admin-panel/MarkSheetMarksManagement";
import EnrolmentTemplate from "components/templates/EnrolmentTemplate";
import MeritListManagement from "pages/admin/admin-panel/MeritListManagement";
import NoticeboardListManagement from "pages/admin/admin-panel/NoticeboardListManagement";
import TcManagement from "pages/admin/admin-panel/TcManagement";
import ServicesDashboard from "pages/admin/admin-panel/ServicesDashboard";
import PramoteStudentManagement from "pages/admin/admin-panel/PramoteStudentManagement";
import SchoolWebsiteManagement from "pages/admin/admin-panel/SchoolWebsiteManagement";
import MarksheetManagement from "pages/admin/admin-panel/MarkSheetMarksManagement";
import AuditLogManagement from "components/admin-panel/AuditLogManagement";
import ScrollToTop from "./components/common/ScrollToTop";
import LandingPage from "./pages/LandingPage";
import SchoolLandingPage from "./pages/SchoolLandingPage";
import ContactUs from "./pages/ContactUs";
import { getSubdomain } from "./utils/subdomain";
import { fetchSchoolByDomain } from "./services/publicSchoolService";
import FeeStructureManagement from "pages/admin/admin-panel/FeeStructureManagement";
import RollNumberManagement from "pages/admin/admin-panel/RollNumberManagement";
import SchoolRegisterManagement from "pages/admin/admin-panel/SchoolRegisterManagement";
import StudentAccountsManagement from "pages/admin/admin-panel/StudentAccountsManagement";
import FeeReceiptsManagement from "pages/admin/admin-panel/FeeReceiptsManagement";
import FeeTransactionsManagement from "pages/admin/admin-panel/FeeTransactionsManagement";
import HolidayCalendarManagement from "pages/admin/admin-panel/HolidayCalendarManagement";
import StudentUpdateRequestsPage from "pages/student/StudentUpdateRequestsPage";
import MarksheetTemplateBuilderPage from "pages/admin/admin-panel/MarksheetTemplateBuilderPage";
import HouseManagement from "pages/admin/admin-panel/HouseManagement";
import CertificateTemplateBuilderPage from "pages/admin/admin-panel/CertificateTemplateBuilderPage";
import CertificateManagementPage from "pages/admin/admin-panel/CertificateManagementPage";
import TeacherAttendancePage from "./pages/teacher-attendance/TeacherAttendancePage";
import AdminAttendancePage from "./pages/teacher-attendance/AdminAttendancePage";
import TaskAssignmentPage from "pages/TaskAssignment/TaskAssignmentPage";
import MyTasksPage from "pages/TaskAssignment/MyTasksPage";
import TimetableManagement from "pages/admin/admin-panel/TimetableManagement";
import StudentTimetablePage from "pages/student/StudentTimetablePage";
import TeacherTimetablePage from "pages/teacher/TeacherTimetablePage";
import MessagingCreditsManagement from "pages/admin/admin-panel/MessagingCreditsManagement";
import MessagingManagement from "pages/school-admin/MessagingManagement";
import HomeworkManagement from "pages/admin/admin-panel/HomeworkManagement";
import StudentHomeworkPage from "pages/student/StudentHomeworkPage";


const App: React.FC = () => {
  const { isLoading, user } = useAuth();
  const { tenantLoading } = useTenantHeader();
  const navigate = useNavigate();
  const subdomain = getSubdomain();
  useJwtExpiration();

  // API-based domain check: only if a subdomain exists, verify it against the backend
  const [domainCheckDone, setDomainCheckDone] = useState(!subdomain);
  const [validSchoolDomain, setValidSchoolDomain] = useState<string | null>(null);



  useEffect(() => {
    if (!subdomain) return;
    fetchSchoolByDomain(subdomain)
      .then(() => setValidSchoolDomain(subdomain))
      .catch(() => setValidSchoolDomain(null))
      .finally(() => setDomainCheckDone(true));
  }, [subdomain]);

  if (isLoading || tenantLoading || !domainCheckDone) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    )
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/" element={validSchoolDomain ? <SchoolLandingPage domainName={validSchoolDomain} /> : <LandingPage />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />
        <Route
          path="/school-selection"
          element={<SchoolSelection />}
        />
        <Route
          path="/student-school-selection"
          element={<SchoolSelectionDashboard />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <ErrorProvider>
      <SchoolProvider>
        <SessionClassProvider>
          <ScrollToTop />
          <Routes>
            {/* Public Routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <Register />
                </PublicRoute>
              }
            />
            <Route
              path="/school-selection"
              element={<SchoolSelection />}
            />
            <Route
              path="/student-school-selection"
              element={<SchoolSelectionDashboard />}
            />

            {/* common routes */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route
                index
                element={
                  user?.roles?.includes("ADMIN") ? (
                    <Navigate to="/admin/dashboard" replace />
                  ) : (
                    <Navigate to="dashboard" replace />
                  )
                }
              />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="students" element={<Students />} />
              <Route path="students/:id" element={<StudentDetail />} />
              <Route path="teachers" element={<Teachers />} />
              <Route path="teachers/:id" element={<TeacherDetail />} />
              <Route path="classes" element={<Classes />} />
              <Route path="classes/:id" element={<ClassDetail />} />
              <Route path="admit-cards" element={<AdmitCards />} />
            </Route>

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute roles={["ADMIN"]}>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route
                index
                element={<Navigate to="/admin/dashboard" replace />}
              />
              <Route path="/admin/users" element={<UserManagement />} />
              <Route path="/admin/schools" element={<SchoolManagement />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route
                path="/admin/website-services"
                element={<SchoolWebsiteManagement />}
              />
              <Route
                path="/admin/messaging-credits"
                element={<MessagingCreditsManagement />}
              />
            </Route>

            {/* School-admin Routes */}
            <Route
              path="/school-admin"
              element={
                <ProtectedRoute roles={["SCHOOL_ADMIN"]}>
                  <Layout isAdmin={false} />
                </ProtectedRoute>
              }
            >
              <Route path="/school-admin/dashboard" element={<Dashboard />} />
              <Route
                path="/school-admin/classes"
                element={<ClassManagement />}
              />
              <Route
                path="/school-admin/sessions"
                element={<SessionManagement />}
              />
              <Route
                path="/school-admin/houses"
                element={<HouseManagement />}
              />
              <Route
                path="/school-admin/application-form"
                element={<ApplicationFormManagement />}
              />
              <Route
                path="/school-admin/application-user"
                element={<ApplicationUserManagement />}
              />
              <Route
                path="/school-admin/students"
                element={<StudentManagement />}
              />
              <Route
                path="/school-admin/teachers"
                element={<TeacherManagement />}
              />
              <Route
                path="/school-admin/subjects"
                element={<SubjectManagement />}
              />
              <Route
                path="/school-admin/aadhar-card"
                element={<AadharCardManagement />}
              />
              <Route
                path="/school-admin/admit-cards"
                element={<AdmitCardManagement />}
              />
              <Route
                path="/school-admin/id-card"
                element={<IdCardManagement />}
              />
              <Route
                path="/school-admin/attendance-management"
                element={<AttendanceManagement />}
              />
              <Route
                path="/school-admin/enrollment"
                element={<EnrolmentTemplate />}
              />
              <Route
                path="/school-admin/fee-structure"
                element={<FeeStructureManagement />}
              />
              <Route
                path="/school-admin/holiday-management"
                element={<HolidayCalendarManagement />}
              />
              <Route
                path="/school-admin/tenth-exam-form"
                element={<ExamFormManagement />}
              />

              <Route
                path="/school-admin/monthly-subject-management"
                element={<GosvaraSheetManagement />}
              />
              <Route
                path="/school-admin/create-marksheet"
                element={<MarksheetManagement />}
              />
            <Route
              path="/school-admin/marksheet-template-builder"
              element={<MarksheetTemplateBuilderPage />}
            />
            <Route
              path="/school-admin/certificate-template-builder"
              element={<CertificateTemplateBuilderPage />}
            />
            <Route
              path="/school-admin/create-certificate"
              element={<CertificateManagementPage />}
            />
              <Route
                path="/school-admin/school-register-management"
                element={<SchoolRegisterManagement />}
              />
              <Route
                path="/school-admin/gosvara-sheet-management"
                element={<GosvaraSheetManagement />}
              />
              <Route
                path="/school-admin/merit-list-management"
                element={<MeritListManagement />}
              />
              <Route
                path="/school-admin/noticeboard-list-management"
                element={<NoticeboardListManagement />}
              />
              <Route
                path="/school-admin/roll-number"
                element={<RollNumberManagement />}
              />
              <Route
                path="/school-admin/tc-management"
                element={<TcManagement />}
              />
              <Route
                path="/school-admin/pramote-student"
                element={<PramoteStudentManagement />}
              />
              <Route
                path="/school-admin/services"
                element={<ServicesDashboard />}
              />
              <Route
                path="/school-admin/actions-performed"
                element={<AuditLogManagement />}
              />
              <Route
                path="/school-admin/student-accounts"
                element={<StudentAccountsManagement />}
              />
              <Route
                path="/school-admin/fee-receipts"
                element={<FeeReceiptsManagement />}
              />
              <Route
                path="/school-admin/fee-transactions"
                element={<FeeTransactionsManagement />}
              />
              <Route
                path="/school-admin/teacher-attendance"
                element={<AdminAttendancePage />}
              />
              <Route
                path="/school-admin/timetable"
                element={<TimetableManagement />}
              />
              <Route
                path="/school-admin/messaging"
                element={<MessagingManagement />}
              />
              <Route
                index
                element={<Navigate to="/school-admin/dashboard" replace />}
              />
            </Route>

            {/* Principal (SCHOOL_ADMIN) task routes */}
            <Route
              path="/principal"
              element={
                <ProtectedRoute roles={["SCHOOL_ADMIN"]}>
                  <Layout isAdmin={false} />
                </ProtectedRoute>
              }
            >
              <Route
                path="/principal/task-assignment"
                element={<TaskAssignmentPage />}
              />
            </Route>

            {/* Teacher Routes */}
            <Route
              path="/teacher"
              element={
                <ProtectedRoute roles={["TEACHER"]}>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="/teacher/dashboard" element={<Dashboard />} />
              <Route path="/teacher/homework" element={<HomeworkManagement />} />
              <Route path="/teacher/application-form" element={<ApplicationFormManagement />} />
              <Route path="/teacher/students" element={<StudentManagement />} />
              <Route path="/teacher/classes" element={<ClassManagement />} />
              <Route path="/teacher/subjects" element={<SubjectManagement />} />
              <Route
                path="/teacher/aadhar-card"
                element={<AadharCardManagement />}
              />
              <Route
                path="/teacher/tenth-exam-form"
                element={<ExamFormManagement />}
              />
              <Route
                path="/teacher/attendance-management"
                element={<AttendanceManagement />}
              />
              <Route
                path="/teacher/monthly-subject-management"
                element={<GosvaraSheetManagement />}
              />
              {/* <Route path="/teacher/assignment-management" element={<AssignmentManagement />} /> */}


              <Route
                path="/teacher/admit-cards"
                element={<AdmitCardManagement />}
              />
              <Route
                path="/teacher/id-card"
                element={<IdCardManagement />}
              />

              <Route
                path="/teacher/services"
                element={<ServicesDashboard />}
              />

              <Route
                path="/teacher/create-marksheet"
                element={<MarkSheetMarksManagement />}
              />
              <Route
                path="/teacher/marksheet-template-builder"
                element={<MarksheetTemplateBuilderPage />}
              />
              <Route
                path="/teacher/certificate-template-builder"
                element={<CertificateTemplateBuilderPage />}
              />
              <Route
                path="/teacher/create-certificate"
                element={<CertificateManagementPage />}
              />
              <Route
                path="/teacher/gosvara-sheet-management"
                element={<GosvaraSheetManagement />}
              />
              <Route
                path="/teacher/merit-list-management"
                element={<MeritListManagement />}
              />
              <Route
                path="/teacher/noticeboard-list-management"
                element={<NoticeboardListManagement />}
              />
              <Route
                path="/teacher/roll-number"
                element={<RollNumberManagement />}
              />
              <Route
                path="/teacher/tc-management"
                element={<TcManagement />}
              />
              {/* <Route path="/teacher/time-table" element={<TimeTableManagement />} /> */}
              <Route
                path="/teacher/enrollment"
                element={<EnrolmentTemplate />}
              />
              <Route
                path="/teacher/result-management"
                element={<ResultManagement />}
              />
              <Route
                path="/teacher/pramote-student"
                element={<PramoteStudentManagement />}
              />
              <Route
                path="/teacher/sessions"
                element={<SessionManagement />}
              />
              <Route
                path="/teacher/student-accounts"
                element={<StudentAccountsManagement />}
              />
              <Route
                path="/teacher/fee-receipts"
                element={<FeeReceiptsManagement />}
              />
              <Route
                path="/teacher/fee-transactions"
                element={<FeeTransactionsManagement />}
              />
              <Route
                path="/teacher/student-update-requests"
                element={<StudentUpdateRequestsPage />}
              />
              <Route
                path="/teacher/teacher-attendance"
                element={<TeacherAttendancePage />}
              />
              <Route
                path="/teacher/my-tasks"
                element={<MyTasksPage />}
              />
              <Route
                path="/teacher/timetable"
                element={<TeacherTimetablePage />}
              />
              <Route
                index
                element={<Navigate to="/teacher/dashboard" replace />}
              />
            </Route>

            <Route
              path="/student/select-profile"
              element={
                <ProtectedRoute roles={["STUDENT"]}>
                  <StudentProfilePicker />
                </ProtectedRoute>
              }
            />

            {/* Student Routes */}
            <Route
              path="/student"
              element={
                <ProtectedRoute roles={["STUDENT"]}>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="/student/dashboard" element={<Dashboard />} />
              <Route
                path="/student/admit-cards"
                element={<AdmitCardManagement />}
              />
              <Route
                path="/student/aadhar-card"
                element={<AadharCardManagement />}
              />
              <Route
                path="/student/exam-form"
                element={<ExamFormManagement />}
              />
              <Route
                path="/student/marksheet-marks"
                element={<MarkSheetMarksManagement />}
              />
              <Route
                path="/student/tc-list"
                element={<TcManagement />}
              />
              <Route
                path="/student/merit-list"
                element={<MeritListManagement />}
              />
              <Route
                path="/student/noticeboard-list"
                element={<NoticeboardListManagement />}
              />
              <Route
                path="/student/profile"
                element={<StudentProfile />}
              />
              <Route path="/student/results" element={<ResultManagement />} />
              <Route
                path="/student/fee-payment"
                element={<FeePaymentManagement />}
              />
              <Route
                path="/student/promotion"
                element={<StudentPromotion />}
              />
              <Route
                path="/student/timetable"
                element={<StudentTimetablePage />}
              />
              <Route
                path="/student/student-update-requests"
                element={<StudentUpdateRequestsPage />}
              />
              <Route
                path="/student/homework"
                element={<StudentHomeworkPage />}
              />
              <Route
                index
                element={<Navigate to="/student/dashboard" replace />}
              />
            </Route>

            {/* APP_USER Routes */}
            <Route
              path="/application-form"
              element={
                <ProtectedRoute roles={["APP_USER"]}>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route path="dashboard" element={<Dashboard />} />
              <Route index element={<AdmissionApplicationFromManagement />} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </SessionClassProvider>
      </SchoolProvider>
    </ErrorProvider>
  );
};

export default App;

